import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppShell } from "../../components/Sidebar";
import { StatusBadge } from "../../components/StatusBadge";
import { api } from "../../lib/api";
import { Search, BookOpen, CheckCircle2, Clock4, Circle, Sparkles } from "lucide-react";

// Original English status/subject keys used for filtering. We match module.status
// (which is always the English source value) against these keys.
const STATUS_KEYS  = ["All Status", "Completed", "In Progress", "Not Started", "Coming Soon"];
const SUBJECT_KEYS = ["All Subjects", "Science", "Maths", "Maths - Ganita Prakash I", "Maths - Ganita Prakash II", "Maths - Ganita Manjari I", "Maths - Ganita Manjari II"];

export default function StudentModules() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All Status");
  const [subject, setSubject] = useState("All Subjects");

  useEffect(() => {
    api.get(`/student/modules?lang=${i18n.resolvedLanguage || "en"}`).then((r) => setData(r.data));
  }, [i18n.resolvedLanguage]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.modules.filter((m) =>
      (!search || m.module_name.toLowerCase().includes(search.toLowerCase())) &&
      (status === "All Status" || m.status === status) &&
      (subject === "All Subjects" || m.subject === subject)
    );
  }, [data, search, status, subject]);

  if (!data) return <AppShell role="student"><p className="text-slate-500">{t("common.loading")}</p></AppShell>;

  const s = data.summary;

  const statusLabel  = (k) => (k === "All Status"  ? t("student.modules.filter_status")  : t(`status.${k}`, { defaultValue: k }));
  const subjectLabel = (k) => (k === "All Subjects" ? t("student.modules.filter_subject") : t(`subjects.${k}`, { defaultValue: k }));

  return (
    <AppShell role="student">
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{t("student.modules.title")}</h1>
      <p className="mt-1 text-slate-500">{t("student.modules.subtitle")}</p>

      <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <SummaryTile icon={BookOpen} color="bg-indigo-100 text-indigo-600" label={t("student.modules.total")} value={s.total} />
        <SummaryTile icon={CheckCircle2} color="bg-emerald-100 text-emerald-600" label={t("status.Completed")} value={s.completed} />
        <SummaryTile icon={Clock4} color="bg-sky-100 text-sky-600" label={t("student.modules.in_progress")} value={s.in_progress} />
        <SummaryTile icon={Circle} color="bg-slate-100 text-slate-500" label={t("student.modules.not_started")} value={s.not_started} />
        <SummaryTile icon={Sparkles} color="bg-violet-100 text-violet-600" label={t("student.modules.coming_soon")} value={s.coming_soon} />
      </div>

      <div className="mt-5 rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
        <p className="text-sm font-medium text-slate-700">{t("student.modules.overall_progress")}</p>
        <div className="mt-2 flex items-center gap-3">
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all" style={{ width: `${s.overall_progress}%` }} />
          </div>
          <span className="text-sm font-bold text-slate-900">{s.overall_progress}%</span>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("common.search")} data-testid="search-modules" className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
          </div>
          <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium outline-none focus:border-indigo-500">
            {STATUS_KEYS.map((o) => <option key={o} value={o}>{statusLabel(o)}</option>)}
          </select>
          <select data-testid="filter-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium outline-none focus:border-indigo-500">
            {SUBJECT_KEYS.map((o) => <option key={o} value={o}>{subjectLabel(o)}</option>)}
          </select>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[800px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="py-3 pr-4">#</th>
                <th className="py-3 pr-4">{t("student.modules.table.module_name")}</th>
                <th className="py-3 pr-4">{t("student.modules.table.subject")}</th>
                <th className="py-3 pr-4">{t("student.modules.table.status")}</th>
                <th className="py-3 pr-4">{t("student.modules.table.completion")}</th>
                <th className="py-3 pr-4">{t("student.modules.table.last_accessed")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m, i) => (
                <tr
                  key={m.module_id}
                  onClick={() => navigate(`/student/modules/${m.module_id}`)}
                  data-testid={`student-module-row-${m.module_id}`}
                  className="cursor-pointer border-b border-slate-50 transition-colors hover:bg-slate-50/60"
                >
                  <td className="py-4 pr-4 text-slate-500">{i + 1}</td>
                  <td className="py-4 pr-4 font-medium text-slate-900">{m.module_name}</td>
                  <td className="py-4 pr-4 text-slate-700">{t(`subjects.${m.subject}`, { defaultValue: m.subject })}</td>
                  <td className="py-4 pr-4"><StatusBadge value={m.status} /></td>
                  <td className="py-4 pr-4">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-28 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full bg-indigo-600" style={{ width: `${m.completion}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-slate-600">{m.completion}%</span>
                    </div>
                  </td>
                  <td className="py-4 pr-4 text-slate-600">{m.last_accessed || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="py-12 text-center text-sm text-slate-500">{t("student.modules.no_match")}</p>}
        </div>
      </div>
    </AppShell>
  );
}

const SummaryTile = ({ icon: Icon, color, label, value }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-card">
    <div className="flex items-center gap-3">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${color}`}><Icon size={18} /></div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-xl font-bold text-slate-900">{value}</p>
      </div>
    </div>
  </div>
);
